import { useCallback, useEffect, useState } from 'react'
import { History, Search, X } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { fetchConfigHistory, fetchConfigActions, type ConfigChangeRow } from './configHistoryApi'

/**
 * A24.22 — Config o'zgarishlar tarixi. Feature-flag, modul, tizim-sozlama,
 * eksperiment va auto-incident kabi TARQOQ sozlama o'zgarishlarini BITTA
 * hisobdorlik ko'rinishiga jamlaydi (admin_audit ustidan config-action lens).
 * "Nega xatti-harakat o'zgardi?" — shu yerda kim/nima/qachon ko'rinadi.
 * Read-only. i18n HOT fayllarga tegmaslik uchun matnlar lokal.
 */

const S = {
  title: 'O’zgarishlar tarixi',
  desc: 'Barcha konfiguratsiya o’zgarishlari (flag, modul, tizim-sozlama, eksperiment)',
  action: 'Amal',
  anyAction: 'Har qanday amal',
  staff: 'Xodim',
  key: 'Kalit',
  since: 'Dan',
  until: 'Gacha',
  search: 'Qidirish',
  clear: 'Tozalash',
  hint: 'Config o’zgarishlarini amal/xodim/kalit/sana bo’yicha qidiring.',
  empty: 'Hech narsa topilmadi',
  results: (n: number) => `${n} ta o’zgarish`,
  time: 'Vaqt',
  value: 'Yangi qiymat',
  ip: 'IP',
}

// Config amallarni o'zbekcha yorliqqa moslaydi (backend action → label).
const ACTION_LABEL: Record<string, string> = {
  set_system_setting: 'Tizim sozlamasi',
  set_feature_flag: 'Flag o’zgardi',
  set_feature_segment: 'Flag segmenti',
  clear_feature_segment: 'Segment tozalandi',
  set_feature_deps: 'Flag bog’liqligi',
  clear_feature_deps: 'Bog’liqlik tozalandi',
  set_feature_schema: 'Flag sxemasi',
  clear_feature_schema: 'Sxema tozalandi',
  set_experiment: 'Eksperiment',
  delete_experiment: 'Eksperiment o’chirildi',
  module_toggle: 'Modul yoq/o’chir',
  auto_incident_toggle: 'Auto-incident',
}
const actionLabel = (a: string) => ACTION_LABEL[a] ?? a

function dayStart(v: string): number | undefined {
  if (!v) return undefined
  const t = new Date(`${v}T00:00:00`).getTime()
  return Number.isFinite(t) ? t : undefined
}
function dayEnd(v: string): number | undefined {
  if (!v) return undefined
  const t = new Date(`${v}T23:59:59`).getTime()
  return Number.isFinite(t) ? t : undefined
}
function fmtTime(ts: number): string {
  if (!Number.isFinite(ts)) return '—'
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return '—'
  }
}

export function ConfigHistory() {
  const [actions, setActions] = useState<string[]>([])
  const [action, setAction] = useState('')
  const [admin, setAdmin] = useState('')
  const [key, setKey] = useState('')
  const [since, setSince] = useState('')
  const [until, setUntil] = useState('')
  const [rows, setRows] = useState<ConfigChangeRow[] | null>(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    fetchConfigHistory({ action, admin, key, since: dayStart(since), until: dayEnd(until), limit: 300 })
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [action, admin, key, since, until])

  // Config-tarix — asosiy ish, ochilishда darhol yuklanadi (PII/audit'дан farqli:
  // maxfiy emas, umumiy hisobdorlik ko'rinishi).
  useEffect(() => {
    fetchConfigActions()
      .then(setActions)
      .catch(() => setActions([]))
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clear = () => {
    setAction('')
    setAdmin('')
    setKey('')
    setSince('')
    setUntil('')
  }

  const input = 'rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 text-sm text-primary outline-none'

  return (
    <>
      <PageHeader title={S.title} screenId="A24.22" description={S.desc} />
      <div className="border-b border-border-subtle p-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tertiary">{S.action}</span>
            <select value={action} onChange={(e) => setAction(e.target.value)} className={input}>
              <option value="">{S.anyAction}</option>
              {actions.map((a) => (
                <option key={a} value={a}>{actionLabel(a)}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tertiary">{S.staff}</span>
            <input value={admin} onChange={(e) => setAdmin(e.target.value)} placeholder="username" className={input} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tertiary">{S.key}</span>
            <input value={key} onChange={(e) => setKey(e.target.value)} placeholder="maintenance_mode" className={input} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tertiary">{S.since}</span>
            <input type="date" value={since} onChange={(e) => setSince(e.target.value)} className={input} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tertiary">{S.until}</span>
            <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className={input} />
          </label>
          <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50">
            <Search size={14} />
            {S.search}
          </button>
          <button onClick={clear} className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-3 py-1.5 text-sm text-secondary hover:text-primary">
            <X size={14} />
            {S.clear}
          </button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {rows === null || loading ? (
          <div className="p-6 text-center text-sm text-tertiary">…</div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-10 text-center text-sm text-tertiary">
            <History size={24} className="mx-auto mb-2 opacity-60" />
            {S.empty}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="mb-2 text-xs text-tertiary">{S.results(rows.length)}</div>
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-border-default text-left text-xs text-tertiary">
                  <th className="px-2 py-1.5 font-medium">{S.time}</th>
                  <th className="px-2 py-1.5 font-medium">{S.staff}</th>
                  <th className="px-2 py-1.5 font-medium">{S.action}</th>
                  <th className="px-2 py-1.5 font-medium">{S.key}</th>
                  <th className="px-2 py-1.5 font-medium">{S.value}</th>
                  <th className="px-2 py-1.5 font-medium">{S.ip}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={String(r.id ?? `${r.created_at}:${r.action}:${r.key ?? ''}`)} className="border-b border-border-subtle align-top">
                    <td className="whitespace-nowrap px-2 py-1.5 text-tertiary">{fmtTime(r.created_at)}</td>
                    <td className="px-2 py-1.5 text-secondary">{r.admin ?? '—'}</td>
                    <td className="px-2 py-1.5">
                      <span className="inline-flex items-center rounded-full border border-border-default bg-surface-2 px-2 py-0.5 text-xs font-medium text-secondary">
                        {actionLabel(r.action)}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 font-mono text-primary">{r.key ?? '—'}</td>
                    <td className="max-w-xs truncate px-2 py-1.5 font-mono text-secondary" title={r.value ?? ''}>{r.value ?? '—'}</td>
                    <td className="px-2 py-1.5 font-mono text-tertiary">{r.ip ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
